<template>
  <div class="manager-company-users-ui">
    <UserResourceManage
      :model="userResourceModel"
      :patch-data-client="patchDataClient"
      :auth-user="authUser"
      :full-control="true"
      list-item-description="user entirely.
      In case of external integration the user may appear again during resources synchronization.
      Please consider suspending user instead."
      variation-class-name="manager-user-resource"
      :refetch-trigger="refetchTrigger"
      :payload="payload"
    >
      <template #table-header>
        <VTableHeaderSortable
          :titles="TABLE_HEADERS"
          variation-class-name="manager-user-resource"
          @sort="onSort"
        />
      </template>
      <template #extra-controls>
        <div class="manager-company-users-ui__extra-controls">
          <div class="manager-company-users-ui__extra-controls-filters-btn">
            <FiltersBtn :is-active="areFiltersApplied" @click="openFiltersModal" />
          </div>
          <SimpleDropdown
            floating
            class="user-resource-manage"
            placement="bottom-end"
            :auto-resize="false"
            :options="bulkActionOptions"
          >
            <template #default="{ toggle }">
              <VBtn flat class="sync" @click="toggle">Actions</VBtn>
            </template>
          </SimpleDropdown>
        </div>
      </template>
      <template #row="{ item }">
        <EditableListText class="slim" :is-disabled="!item.is_active">
          <span class="manager-company-users-ui__title" :title="item.email">{{ item.email }}</span>
          <ManagerCompanyUsersSendEmail
            :user-id="item.id"
            :model="managerUserModel"
            :is-disabled="!item.is_active"
          />
          <div>
            <ManagerCompanyUsersLoginLink
              :user-id="item.id"
              :model="managerUserModel"
              :is-disabled="!item.is_active"
            />
          </div>
        </EditableListText>
        <EditableListText class="slim" :is-disabled="!item.is_active">{{
          item.name
        }}</EditableListText>
        <EditableListText class="slim" :is-disabled="!item.is_active" multi-line>
          <p v-if="item.patches.length" class="manager-company-users-ui__patches-total">Total areas: {{ item.patches.length }}</p>
          {{ patchesString(item.patches) }}</EditableListText
        >
        <EditableListText class="slim">
          <!-- @vue-ignore TODO: adjust user resource types, "subscribed" present for manager only -->
          <ManagerCompanyUsersSubscription
            v-model="item.subscriptions"
            class="slim"
            :user-id="item.id"
            :user-name="item.name"
            :user-email="item.email"
            :model="managerUserModel"
            :is-disabled="!item.is_active"
            :after-success-callback="fetchStats"
            :type="ESubscriptionType.USER_SALE_OPPORTUNITY"
          />
        </EditableListText>
        <EditableListText class="slim">
          <!-- @vue-ignore TODO: adjust user resource types, "subscribed" present for manager only -->
          <ManagerCompanyUsersSubscription
            v-model="item.subscriptions"
            class="slim"
            :user-id="item.id"
            :user-name="item.name"
            :user-email="item.email"
            :model="managerUserModel"
            :is-disabled="!item.is_active"
            :after-success-callback="fetchStats"
            :type="ESubscriptionType.USER_LET_OPPORTUNITY"
          />
        </EditableListText>
        <EditableListText class="slim" :is-disabled="!item.is_active">{{
          item.role
        }}</EditableListText>
        <EditableListText>
          <VBtn
            outline
            class="manager-company-users-ui__status slim"
            title="Toggles if client allowed to use app"
            @click="toggleIsActive(item)"
          >
            <i
              :class="[
                'fr',
                `fr-${item.is_active ? 'check-circle' : 'cancel'}`,
                'glyph',
                `manager-company-users-ui__status--${item.is_active ? 'active' : 'suspended'}`,
              ]"
            ></i>
          </VBtn>
        </EditableListText>
        <EditableListText multi-line class="slim">{{
          item.last_visit ? DateTimeFormatter.toDateTime(item.last_visit) : '-'
        }}</EditableListText>
      </template>
    </UserResourceManage>
  </div>
</template>

<script setup lang="ts">
  // Types
  import type {
    IUserResourceModel,
    TUser,
    TUserIndexPayload,
    TUserPatch,
  } from '~/misc/user-resource/types';
  import type { TUser as TAuthUser } from '~/misc/auth/types';
  import type { TPatch, TPatchDataClient } from '~/misc/patch/types';
  import type { TSimpleDropdownOption } from '~/misc/simple-dropdown/types';
  import type { TBulkActionOptionsConfigItem } from '~/modules/manager/company/users/types';
  import type { TSortingState } from '~/misc/v-table-header-sortable/types';
  import type { TManagerCompanyUsersFilters } from '~/modules/manager/company/users/filters/types';
  import { EManagerCompanyUsersBulkAction } from '~/modules/manager/company/users/types';
  import { ESubscriptionType } from '~/modules/manager/types';

  // Config
  import {
    BULK_ACTION_OPTIONS_CONFIG,
    TABLE_HEADERS,
  } from '~/modules/manager/company/users/config';

  // Services
  import { ManagerUserResourceAdapter } from '~/modules/manager/models/user/user-resource-adapter';
  import { ManagerPatchDataClient } from '~/modules/manager/services/manager-patch-data-client';
  import { PatchLabelResolver } from '~/misc/patch/services/label-resolver';
  import { DateTimeFormatter } from '~/misc/utils/datetime';

  // Models
  import { ManagerUserModel } from '~/modules/manager/models/user';

  // Composables
  import { useToast } from '~/composables/use-toast';
  import { useManagerCompanyUsersBulkActions } from '~/modules/manager/company/users/composables/manager-company-users-bulk-actions';

  // Components
  import UserResourceManage from '~/misc/user-resource/manage/UserResourceManage.vue';
  import SimpleDropdown from '~/misc/simple-dropdown/SimpleDropdown.vue';
  import EditableListText from '~/misc/editable-list/text/EditableListText.vue';
  import ManagerCompanyUsersSendEmail from '~/modules/manager/company/users/send-email/ManagerCompanyUsersSendEmail.vue';
  import ManagerCompanyUsersLoginLink from '~/modules/manager/company/users/login-link/ManagerCompanyUsersLoginLink.vue';
  import ManagerCompanyUsersSubscription from '~/modules/manager/company/users/subscription/ManagerCompanyUsersSubscription.vue';
  import VTableHeaderSortable from '~/misc/v-table-header-sortable/VTableHeaderSortable.vue';
  import FiltersBtn from '~/misc/btn/filters/FiltersBtn.vue';
  import VBtn from '~/misc/v-btn';

  const ConfirmModal = defineAsyncComponent(
    () => import('~/misc/resource/confirm/modal/ConfirmModal.vue')
  );

  const ManagerCompanyUsersFiltersModal = defineAsyncComponent(
    () =>
      import('~/modules/manager/company/users/filters/modal/ManagerCompanyUsersFiltersModal.vue')
  );

  const props = defineProps({
    companyId: {
      type: Number,
      required: true,
    },
    authUser: {
      type: Object as PropType<TAuthUser | null>,
      default: null,
    },
  });

  const {
    statuses,
    fetchStats,
    handleEnableAll,
    handleDisableAll,
    handleSubscribeAll,
    handleUnsubscribeAll,
  } = useManagerCompanyUsersBulkActions(props.companyId);

  const BULK_ACTIONS: Record<EManagerCompanyUsersBulkAction, () => Promise<unknown>> = {
    [EManagerCompanyUsersBulkAction.ENABLE]: handleEnableAll,
    [EManagerCompanyUsersBulkAction.DISABLE]: handleDisableAll,
    [EManagerCompanyUsersBulkAction.SUBSCRIBE_SALES]: () =>
      handleSubscribeAll(ESubscriptionType.USER_SALE_OPPORTUNITY),
    [EManagerCompanyUsersBulkAction.UNSUBSCRIBE_SALES]: () =>
      handleUnsubscribeAll(ESubscriptionType.USER_SALE_OPPORTUNITY),
    [EManagerCompanyUsersBulkAction.SUBSCRIBE_LETTINGS]: () =>
      handleSubscribeAll(ESubscriptionType.USER_LET_OPPORTUNITY),
    [EManagerCompanyUsersBulkAction.UNSUBSCRIBE_LETTINGS]: () =>
      handleUnsubscribeAll(ESubscriptionType.USER_LET_OPPORTUNITY),
  };

  const bulkActionOptions: ComputedRef<TSimpleDropdownOption[]> = computed<TSimpleDropdownOption[]>(
    () =>
      BULK_ACTION_OPTIONS_CONFIG.map((item: TBulkActionOptionsConfigItem) => ({
        title: item.title,
        disabled: !statuses.value[item.action],
        clickCallback: () => openBulkActionModal(item.action),
      }))
  );

  const { show: showModal } = useModal();
  const { error, success } = useToast();
  const managerUserModel: ManagerUserModel = new ManagerUserModel();

  const userResourceModel: IUserResourceModel = new ManagerUserResourceAdapter(
    props.companyId,
    managerUserModel
  );
  const patchDataClient: TPatchDataClient = new ManagerPatchDataClient(props.companyId);

  const refetchTrigger: Ref<number> = ref(0);

  const payload: Ref<TUserIndexPayload> = ref<TUserIndexPayload>({});

  const areFiltersApplied: ComputedRef<boolean> = computed<boolean>(
    () =>
      !!payload.value.filters &&
      Object.values(payload.value.filters).some((value: number | undefined) => value !== undefined)
  );

  const setPayload = (value: TUserIndexPayload) => {
    payload.value = {
      ...payload.value,
      ...value,
    };
  };

  const onSort = (value: TSortingState | null) => {
    if (!value) {
      setPayload({
        sorting: undefined,
      });

      return;
    }

    setPayload({
      sorting: value,
    });
  };

  const patchesString = (patches: TUserPatch[]): string =>
    PatchLabelResolver.getListLabel(patches as TPatch[]);

  const toggleIsActive = (user: TUser) => {
    (user.is_active ? managerUserModel.suspend(user.id) : managerUserModel.activate(user.id))
      .then(() => {
        fetchStats();
        success({ message: 'Done' });
        user.is_active = !user.is_active;
      })
      .catch(() => {
        error({ message: 'Something went wrong' });
      });
  };

  const triggerRefetch = () => refetchTrigger.value++;

  const getBulkConfirmationTitle = (action: EManagerCompanyUsersBulkAction): string => {
    return `You're going to ${action} all users`;
  };

  const openBulkActionModal = (action: EManagerCompanyUsersBulkAction) =>
    showModal(ConfirmModal, {
      title: getBulkConfirmationTitle(action),
      confirmedCallback: async () => {
        try {
          await BULK_ACTIONS[action]();
          triggerRefetch();
          fetchStats();
        } catch {
          error({ message: 'Sorry, something went wrong' });
        }
      },
    });

  const openFiltersModal = () => {
    showModal(ManagerCompanyUsersFiltersModal, {
      currentFilters: payload.value.filters || {},
      updatedCallback: (filters: TManagerCompanyUsersFilters) => setPayload({ filters }),
    });
  };
</script>
